import { Injectable } from '@angular/core';
import { GuacamoleClientManagerService } from '../../../services/guacamole-client-manager.service';
import { VmSessionManagerService, VmSessionData } from '../../../services/vm-session-manager.service';
import { IShortLogger, Log } from '../../../services/logger.service';
import { SharedConnectionResponse } from '../../../models/shared-connection.model';

/**
 * Test service that extends existing Guacamole functionality for manual testing.
 * Creates encrypted tokens and manages test sessions without modifying core services.
 */
@Injectable({
  providedIn: 'root'
})
export class VmConnectionService {
  private readonly logger: IShortLogger<VmConnectionService> = Log.create(VmConnectionService);

  constructor(
    private guacamoleManager: GuacamoleClientManagerService,
    private sessionManager: VmSessionManagerService
  ) { }

  /**
   * Generate encrypted token using the same method as reference implementation
   * This replicates the generateGuacamoleToken function from token-generator.js
   */
  async generateGuacamoleToken(tokenObj: any): Promise<string> {
    const KEY = new TextEncoder().encode('MySuperSecretKeyForParamsToken12');

    const iv = crypto.getRandomValues(new Uint8Array(16));
    const algo = { name: "AES-CBC", iv };
    const key = await crypto.subtle.importKey("raw", KEY, algo, false, ["encrypt"]);
    const ct = new Uint8Array(await crypto.subtle.encrypt(algo, key,
      new TextEncoder().encode(JSON.stringify(tokenObj))));

    const token = btoa(JSON.stringify({
      iv: btoa(String.fromCharCode(...iv)),
      value: btoa(String.fromCharCode(...ct))
    }));

    return token;
  }

  /**
   * Create connection settings based on protocol and user inputs with optimal resolution
   */
  createConnectionSettings(protocol: 'vnc' | 'rdp', hostname: string, username: string, password: string, resolution?: { width: number, height: number }): any {
    const baseSettings = {
      hostname,
      username,
      password
    };

    // Use provided resolution or fallback to defaults
    const width = resolution?.width || 1280;
    const height = resolution?.height || 720;

    if (protocol === 'rdp') {
      return {
        ...baseSettings,
        "ignore-cert": true,
        "security": "any",
        "enable-drive": true,
        "drive-path": "/tmp/guac-drive",
        "create-drive-path": true,
        "enable-printing": true,
        "audio": ["audio/L16;rate=44100"],
        "width": width,   // Use optimal resolution
        "height": height, // Use optimal resolution
        "dpi": 96         // Set DPI for consistent scaling
      };
    } else { // vnc - match backend exactly
      return {
        hostname,
        username,
        password,
        port: 5900,
        autoretry: 3,
        color_depth: 24,
        swap_red_blue: false,
        connect_timeout: 15,
        width: width,   // Use optimal resolution
        height: height, // Use optimal resolution
        dpi: 96         // Fixed DPI (matches backend)
      };
    }
  }

  /**
   * Disconnect test connection
   */
  disconnectTestVm(): void {
    const testClassId = 'test-class';
    const testUserId = 'test-user';

    // Remove client instance
    this.guacamoleManager.removeClient(testClassId, testUserId);

    // Clear session data
    this.sessionManager.clearSession(testClassId, testUserId);

    this.logger.d("Test VM connection disconnected");
  }

  /**
   * Connect using existing session data for production mode
   */
  async connectWithExistingSession(
    classId: string,
    userId: string,
    displayElement: HTMLElement,
    resolution?: { width: number, height: number }
  ): Promise<any> {
    try {
      this.logger.d(`Connecting with existing session: ${classId}/${userId}`);

      // Use existing Guacamole client manager for production connections
      const clientInstance = await this.guacamoleManager.createAndInitializeClient(
        classId,
        userId,
        displayElement
      );

      this.logger.d('Production connection established successfully');
      return clientInstance;

    } catch (error) {
      this.logger.e('Failed to connect with existing session:', error);
      throw error;
    }
  }

  /**
   * Connect using credentials fetched directly by the child window — no localStorage read.
   * Use when the vm-connection component bootstraps with ctx/role/un URL params and has
   * already obtained sessionData from the backend itself.
   */
  async connectWithProvidedSessionData(
    classId: string,
    userId: string,
    sessionData: VmSessionData,
    displayElement: HTMLElement,
    resolution?: { width: number, height: number }
  ): Promise<any> {
    try {
      this.logger.d(`Connecting with provided session data: ${classId}/${userId}`);
      const clientInstance = await this.guacamoleManager.createAndInitializeClientWithData(
        classId,
        userId,
        sessionData,
        displayElement
      );
      this.logger.d('Connection established with provided session data');
      return clientInstance;
    } catch (error) {
      this.logger.e('Failed to connect with provided session data:', error);
      throw error;
    }
  }

  /**
   * Connect using shared session (trainer joining student's session)
   * Uses Guacamole connection multiplexing to join existing RDP session
   */
  async connectWithExistingSharedSession(
    sharedConnectionInfo: SharedConnectionResponse,
    studentClassId: string,
    studentUserId: string,
    trainerUserId: string,
    displayElement: HTMLElement,
    resolution?: { width: number, height: number }
  ): Promise<any> {
    try {
      this.logger.d(`Trainer connecting to shared session: ${studentClassId}/${studentUserId}`);
      this.logger.d(`Shared connection ID: ${sharedConnectionInfo.connectionId}`);

      // Use Guacamole client manager to create shared client
      const clientInstance = await this.guacamoleManager.createAndInitializeSharedClient(
        sharedConnectionInfo,
        studentClassId,
        studentUserId,
        trainerUserId,
        displayElement
      );

      this.logger.d('Shared session connection established successfully (Trainer joined)');
      return clientInstance;

    } catch (error) {
      this.logger.e('Failed to connect to shared session:', error);
      throw error;
    }
  }

  /**
   * Disconnect production connection
   */
  disconnectProductionVm(classId: string, userId: string): void {
    // Remove client instance
    this.guacamoleManager.removeClient(classId, userId);

    // Note: Session data is NOT cleared here to allow reconnect on page reload
    // Session is only cleared on intentional disconnect (from component's confirmDisconnect)

    this.logger.d(`Production VM connection disconnected: ${classId}/${userId}`);
  }

  /**
   * Get current active Guacamole client for keystrokes (works for both test and production)
   */
  getCurrentClient(classId?: string, userId?: string): any {
    // Use provided session identifiers or fallback to test defaults
    const sessionClassId = classId || 'test-class';
    const sessionUserId = userId || 'test-user';

    const clientInstance = this.guacamoleManager.getClient(sessionClassId, sessionUserId);
    return clientInstance?.getGuacamoleClient();
  }

  /**
   * Get the GuacamoleClientInstance for a session.
   * Gives callers access to richer operations such as sendSize() and setting
   * reconnect callbacks (onReconnecting / onReconnected / onConnectionFailed).
   *
   * @param classId - Class identifier (defaults to 'test-class' in test mode)
   * @param userId  - User identifier  (defaults to 'test-user'  in test mode)
   */
  getClientInstance(classId?: string, userId?: string) {
    const sessionClassId = classId || 'test-class';
    const sessionUserId  = userId  || 'test-user';
    return this.guacamoleManager.getClient(sessionClassId, sessionUserId);
  }
}
